import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { abastecimentoFinanceiroService, veiculoService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const AbastecimentoFinanceiroForm = ({ abastecimento, onClose, onSuccess }) => {
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    veiculo: '',
    data: new Date().toISOString().split('T')[0],
    hodometro: '',
    tipoCombustivel: 'gasolina',
    quantidadeLitros: '',
    bombaInicial: '',
    bombaFinal: '',
    usouPosto: false,
    responsavel: user?.id || '',
    observacoes: '',
    posto: {
      nome: '',
      cnpj: '',
      endereco: {
        rua: '',
        numero: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: ''
      }
    },
    nfe: {
      numero: '',
      serie: '',
      chaveAcesso: '',
      dataEmissao: new Date().toISOString().split('T')[0],
      valorNfe: '',
      valorLitro: '',
      valorTotal: ''
    },
    nfeArquivo: null
  });

  const [errors, setErrors] = useState({});
  const [buscaVeiculo, setBuscaVeiculo] = useState('');
  const [mostrarDropdownVeiculos, setMostrarDropdownVeiculos] = useState(false);

  // Buscar veículos
  const { data: veiculosData, isLoading: veiculosLoading } = useQuery({
    queryKey: ['veiculos'],
    queryFn: () => veiculoService.listar({ status: 'ativo', limit: 100 })
  });


  // Garantir que os dados sejam sempre arrays válidos
  const veiculos = useMemo(() => {
    if (!veiculosData) return [];
    if (veiculosData.success && Array.isArray(veiculosData.data)) {
      return veiculosData.data;
    }
    return [];
  }, [veiculosData]);

  // Filtrar veículos baseado na busca
  const veiculosFiltrados = useMemo(() => {
    if (!buscaVeiculo.trim()) return veiculos;
    
    return veiculos.filter(veiculo => 
      veiculo.placa.toLowerCase().includes(buscaVeiculo.toLowerCase()) ||
      veiculo.modelo.toLowerCase().includes(buscaVeiculo.toLowerCase()) ||
      veiculo.marca.toLowerCase().includes(buscaVeiculo.toLowerCase())
    );
  }, [veiculos, buscaVeiculo]);


  // Mutation para criar/atualizar
  const mutation = useMutation({
    mutationFn: (dados) => {
      if (abastecimento) {
        return abastecimentoFinanceiroService.atualizar(abastecimento._id, dados);
      } else {
        return abastecimentoFinanceiroService.criar(dados);
      }
    },
    onSuccess: () => {
      toast.success(abastecimento ? 'Abastecimento atualizado com sucesso!' : 'Abastecimento criado com sucesso!');
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao salvar abastecimento');
    }
  });

  // Carregar dados do abastecimento se estiver editando
  useEffect(() => {
    if (abastecimento) {
      setFormData({
        veiculo: abastecimento.veiculo?._id || '',
        data: abastecimento.data ? new Date(abastecimento.data).toISOString().split('T')[0] : '',
        hodometro: abastecimento.hodometro?.toString() || '',
        tipoCombustivel: abastecimento.tipoCombustivel || 'gasolina',
        quantidadeLitros: abastecimento.quantidadeLitros?.toString() || '',
        bombaInicial: abastecimento.bombaInicial?.toString() || '',
        bombaFinal: abastecimento.bombaFinal?.toString() || '',
        usouPosto: !!(abastecimento.posto?.nome),
        responsavel: user?.id || '',
        observacoes: abastecimento.observacoes || '',
        posto: {
          nome: abastecimento.posto?.nome || '',
          cnpj: abastecimento.posto?.cnpj || '',
          endereco: {
            rua: abastecimento.posto?.endereco?.rua || '',
            numero: abastecimento.posto?.endereco?.numero || '',
            bairro: abastecimento.posto?.endereco?.bairro || '',
            cidade: abastecimento.posto?.endereco?.cidade || '',
            estado: abastecimento.posto?.endereco?.estado || '',
            cep: abastecimento.posto?.endereco?.cep || ''
          }
        },
        nfe: {
          numero: abastecimento.nfe?.numero || '',
          serie: abastecimento.nfe?.serie || '',
          chaveAcesso: abastecimento.nfe?.chaveAcesso || '',
          dataEmissao: abastecimento.nfe?.dataEmissao ? 
            new Date(abastecimento.nfe.dataEmissao).toISOString().split('T')[0] : '',
          valorNfe: abastecimento.nfe?.valorNfe?.toString() || '',
          valorLitro: abastecimento.nfe?.valorLitro?.toString() || '',
          valorTotal: abastecimento.nfe?.valorTotal?.toString() || ''
        },
        nfeArquivo: null
      });
    }
  }, [abastecimento]);

  // Atualizar responsável quando o usuário mudar
  useEffect(() => {
    if (user?.id) {
      setFormData(prev => ({
        ...prev,
        responsavel: user.id
      }));
    }
  }, [user?.id]);

  // Atualizar busca de veículo quando veículo for selecionado
  useEffect(() => {
    if (formData.veiculo && veiculos.length > 0) {
      const veiculoSelecionado = veiculos.find(v => v._id === formData.veiculo);
      if (veiculoSelecionado) {
        setBuscaVeiculo(`${veiculoSelecionado.placa} - ${veiculoSelecionado.modelo}`);
      }
    }
  }, [formData.veiculo, veiculos]);

  // Calcular custo por quilômetro automaticamente
  useEffect(() => {
    if (formData.nfe.valorTotal && formData.hodometro && formData.quantidadeLitros) {
      const valorTotal = parseFloat(formData.nfe.valorTotal);
      const hodometro = parseInt(formData.hodometro);
      const quantidadeLitros = parseFloat(formData.quantidadeLitros);
      
      if (valorTotal > 0 && hodometro > 0 && quantidadeLitros > 0) {
        // Calcular custo por litro
        // const custoPorLitro = valorTotal / quantidadeLitros;
        
        // Calcular custo por km (assumindo que o abastecimento é proporcional ao uso)
        // const custoPorKm = valorTotal / (hodometro / 1000); // Convertendo para km
        
      }
    }
  }, [formData.nfe.valorTotal, formData.hodometro, formData.quantidadeLitros]);

  // Calcular quantidade de litros pela diferença da bomba
  useEffect(() => {
    if (formData.bombaFinal && formData.bombaInicial) {
      const inicial = parseFloat(formData.bombaInicial);
      const final = parseFloat(formData.bombaFinal);
      if (!isNaN(inicial) && !isNaN(final) && final >= inicial) {
        const litros = final - inicial;
        setFormData(prev => ({ ...prev, quantidadeLitros: litros.toFixed(2) }));
      }
    }
  }, [formData.bombaInicial, formData.bombaFinal]);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.veiculo-search-container')) {
        setMostrarDropdownVeiculos(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erro do campo
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleVeiculoSelect = (veiculo) => {
    setFormData(prev => ({
      ...prev,
      veiculo: veiculo._id
    }));
    setBuscaVeiculo(`${veiculo.placa} - ${veiculo.modelo}`);
    setMostrarDropdownVeiculos(false);
    
    // Limpar erro do campo
    if (errors.veiculo) {
      setErrors(prev => ({
        ...prev,
        veiculo: null
      }));
    }
  };

  const handlePostoChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      posto: {
        ...prev.posto,
        [field]: value
      }
    }));
  };

  const handleEnderecoChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      posto: {
        ...prev.posto,
        endereco: {
          ...prev.posto.endereco,
          [field]: value
        }
      }
    }));
  };

  const handleNfeChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      nfe: {
        ...prev.nfe,
        [field]: value
      }
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de arquivo (apenas PDF)
      if (file.type !== 'application/pdf') {
        toast.error('Apenas arquivos PDF são permitidos para NF-e');
        return;
      }
      
      // Validar tamanho (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Arquivo deve ter no máximo 10MB');
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        nfeArquivo: file
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.veiculo) newErrors.veiculo = 'Veículo é obrigatório';
    if (!formData.data) newErrors.data = 'Data é obrigatória';
    if (!formData.hodometro || parseInt(formData.hodometro) < 0) {
      newErrors.hodometro = 'Hodômetro deve ser maior ou igual a zero';
    }
    if (!formData.tipoCombustivel) newErrors.tipoCombustivel = 'Tipo de combustível é obrigatório';
    if (!formData.quantidadeLitros || parseFloat(formData.quantidadeLitros) <= 0) {
      if (!formData.bombaInicial || !formData.bombaFinal) {
        newErrors.quantidadeLitros = 'Informe a quantidade ou os números da bomba';
      }
    }
    // Posto opcional - so obrigatorio se marcou que usou posto
    if (formData.usouPosto && !formData.posto.nome) newErrors['posto.nome'] = 'Nome do posto é obrigatório';
    // NF-e opcional - abastecimento pode ser na propria empresa
    if (!formData.nfe.valorNfe || parseFloat(formData.nfe.valorNfe) < 0) {
      newErrors['nfe.valorNfe'] = 'Valor da NF-e deve ser maior ou igual a zero';
    }
    if (!formData.nfe.valorLitro || parseFloat(formData.nfe.valorLitro) < 0) {
      newErrors['nfe.valorLitro'] = 'Valor por litro deve ser maior ou igual a zero';
    }
    if (!formData.nfe.valorTotal || parseFloat(formData.nfe.valorTotal) < 0) {
      newErrors['nfe.valorTotal'] = 'Valor total deve ser maior ou igual a zero';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    const dados = {
      ...formData,
      quantidadeLitros: parseFloat(formData.quantidadeLitros),
      hodometro: parseInt(formData.hodometro),
      nfe: {
        ...formData.nfe,
        valorNfe: parseFloat(formData.nfe.valorNfe),
        valorLitro: parseFloat(formData.nfe.valorLitro),
        valorTotal: parseFloat(formData.nfe.valorTotal)
      }
    };

    mutation.mutate(dados);
  };

  // const getTipoCombustivelLabel = (tipo) => {
  //   const tipos = {
  //     gasolina: 'Gasolina',
  //     etanol: 'Etanol',
  //     diesel: 'Diesel',
  //     gnv: 'GNV',
  //     eletrico: 'Elétrico'
  //   };
  //   return tipos[tipo] || tipo;
  // };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {abastecimento ? 'Editar Abastecimento' : 'Novo Abastecimento'}
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Veículo */}
                    <div className="veiculo-search-container">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Veículo *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={buscaVeiculo}
                          onChange={(e) => {
                            setBuscaVeiculo(e.target.value);
                            setMostrarDropdownVeiculos(true);
                          }}
                          onFocus={() => setMostrarDropdownVeiculos(true)}
                          placeholder={veiculosLoading ? 'Carregando veículos...' : 'Digite placa, modelo ou marca...'}
                          className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 pr-10 ${
                            errors.veiculo ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setMostrarDropdownVeiculos(!mostrarDropdownVeiculos)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <MagnifyingGlassIcon className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {/* Dropdown de veículos filtrados */}
                      {mostrarDropdownVeiculos && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                          {veiculosFiltrados.length > 0 ? (
                            veiculosFiltrados.map(veiculo => (
                              <button
                                key={veiculo._id}
                                type="button"
                                onClick={() => handleVeiculoSelect(veiculo)}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                              >
                                <div className="font-medium text-gray-900">{veiculo.placa}</div>
                                <div className="text-sm text-gray-500">{veiculo.marca} {veiculo.modelo}</div>
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-2 text-gray-500 text-sm">
                              {buscaVeiculo ? 'Nenhum veículo encontrado' : 'Nenhum veículo cadastrado'}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Mostrar veículo selecionado */}
                      {formData.veiculo && (
                        <div className="mt-2 flex items-center justify-between bg-purple-50 px-3 py-2 rounded-md">
                          <span className="text-sm text-purple-800">
                            {veiculos.find(v => v._id === formData.veiculo)?.placa} - {veiculos.find(v => v._id === formData.veiculo)?.modelo}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              handleInputChange('veiculo', '');
                              setBuscaVeiculo('');
                              setMostrarDropdownVeiculos(false);
                            }}
                            className="text-purple-600 hover:text-purple-800"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      
                      {errors.veiculo && (
                        <p className="mt-1 text-sm text-red-600">{errors.veiculo}</p>
                      )}
                    </div>

                    {/* Data */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Data *
                      </label>
                      <input
                        type="date"
                        value={formData.data}
                        onChange={(e) => handleInputChange('data', e.target.value)}
                        className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                          errors.data ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors.data && (
                        <p className="mt-1 text-sm text-red-600">{errors.data}</p>
                      )}
                    </div>

                    {/* Hodômetro */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hodômetro (km) *
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.hodometro}
                        onChange={(e) => handleInputChange('hodometro', e.target.value)}
                        className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                          errors.hodometro ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Ex: 50000"
                      />
                      {errors.hodometro && (
                        <p className="mt-1 text-sm text-red-600">{errors.hodometro}</p>
                      )}
                    </div>

                    {/* Tipo de Combustível */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de Combustível *
                      </label>
                      <select
                        value={formData.tipoCombustivel}
                        onChange={(e) => handleInputChange('tipoCombustivel', e.target.value)}
                        className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                          errors.tipoCombustivel ? 'border-red-300' : 'border-gray-300'
                        }`}
                      >
                        <option value="gasolina">Gasolina</option>
                        <option value="etanol">Etanol</option>
                        <option value="diesel">Diesel</option>
                        <option value="gnv">GNV</option>
                        <option value="eletrico">Elétrico</option>
                      </select>
                      {errors.tipoCombustivel && (
                        <p className="mt-1 text-sm text-red-600">{errors.tipoCombustivel}</p>
                      )}
                    </div>

                    {/* Números da bomba - calcula quantidade */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nº Inicial da Bomba
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        min="0"
                        value={formData.bombaInicial}
                        onChange={(e) => handleInputChange('bombaInicial', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Ex: 10000.000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nº Final da Bomba
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        min="0"
                        value={formData.bombaFinal}
                        onChange={(e) => handleInputChange('bombaFinal', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Ex: 10050.000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quantidade (L)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={formData.quantidadeLitros}
                        onChange={(e) => handleInputChange('quantidadeLitros', e.target.value)}
                        className={`w-full border rounded-md px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                          errors.quantidadeLitros ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Calculado pela bomba"
                        readOnly={!!formData.bombaInicial && !!formData.bombaFinal}
                      />
                      {errors.quantidadeLitros && (
                        <p className="mt-1 text-sm text-red-600">{errors.quantidadeLitros}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">💡 Preenchido automaticamente pela diferença da bomba</p>
                    </div>


                  </div>

                  {/* Informações do Posto - OPCIONAL */}
                  <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">Informações do Posto</h4>
                        <p className="text-sm text-gray-500 mt-0.5">Abastecimento na própria empresa não precisa</p>
                      </div>
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.usouPosto}
                          onChange={(e) => handleInputChange('usouPosto', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600 relative"></div>
                        <span className="ml-3 text-sm font-medium text-gray-700">
                          {formData.usouPosto ? 'Posto externo' : 'Abastecimento interno'}
                        </span>
                      </label>
                    </div>
                    {formData.usouPosto && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nome do Posto *</label>
                        <input
                          type="text"
                          value={formData.posto.nome}
                          onChange={(e) => handlePostoChange('nome', e.target.value)}
                          className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors['posto.nome'] ? 'border-red-300' : 'border-gray-300'}`}
                          placeholder="Ex: Posto Shell"
                        />
                        {errors['posto.nome'] && (
                          <p className="mt-1 text-sm text-red-600">{errors['posto.nome']}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">CNPJ</label>
                        <input
                          type="text"
                          value={formData.posto.cnpj}
                          onChange={(e) => handlePostoChange('cnpj', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Ex: 00.000.000/0000-00"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Rua</label>
                        <input
                          type="text"
                          value={formData.posto.endereco.rua}
                          onChange={(e) => handleEnderecoChange('rua', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Ex: Av. das Nações"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cidade</label>
                        <input
                          type="text"
                          value={formData.posto.endereco.cidade}
                          onChange={(e) => handleEnderecoChange('cidade', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Ex: Brasília"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                        <input
                          type="text"
                          value={formData.posto.endereco.estado}
                          onChange={(e) => handleEnderecoChange('estado', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Ex: DF"
                          maxLength={2}
                        />
                      </div>
                    </div>
                    )}
                  </div>

                  {/* Informações da NF-e - OPCIONAL */}
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-semibold text-gray-900">Informações da Nota Fiscal</h4>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                        Opcional
                      </span>
                    </div>
                    <p className="text-sm text-indigo-700 mb-4">
                      💡 Preencha apenas se o abastecimento foi feito em posto externo. Abastecimento na própria empresa não precisa de NF-e.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Número da NF-e
                        </label>
                        <input
                          type="text"
                          value={formData.nfe.numero}
                          onChange={(e) => handleNfeChange('numero', e.target.value)}
                          className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                            errors['nfe.numero'] ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Ex: 000123456"
                        />
                        {errors['nfe.numero'] && (
                          <p className="mt-1 text-sm text-red-600">{errors['nfe.numero']}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Série
                        </label>
                        <input
                          type="text"
                          value={formData.nfe.serie}
                          onChange={(e) => handleNfeChange('serie', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Ex: 1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Data de Emissão
                        </label>
                        <input
                          type="date"
                          value={formData.nfe.dataEmissao}
                          onChange={(e) => handleNfeChange('dataEmissao', e.target.value)}
                          className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                            errors['nfe.dataEmissao'] ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        {errors['nfe.dataEmissao'] && (
                          <p className="mt-1 text-sm text-red-600">{errors['nfe.dataEmissao']}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor da NF-e (R$)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.nfe.valorNfe}
                          onChange={(e) => handleNfeChange('valorNfe', e.target.value)}
                          className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                            errors['nfe.valorNfe'] ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Ex: 275.00"
                        />
                        {errors['nfe.valorNfe'] && (
                          <p className="mt-1 text-sm text-red-600">{errors['nfe.valorNfe']}</p>
                        )}
                      </div>
                      
                      {/* Valor por Litro */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor por Litro (R$) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.nfe.valorLitro}
                          onChange={(e) => handleNfeChange('valorLitro', e.target.value)}
                          className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                            errors['nfe.valorLitro'] ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Ex: 5.50"
                        />
                        {errors['nfe.valorLitro'] && (
                          <p className="mt-1 text-sm text-red-600">{errors['nfe.valorLitro']}</p>
                        )}
                      </div>

                      {/* Valor Total */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor Total (R$) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.nfe.valorTotal}
                          onChange={(e) => handleNfeChange('valorTotal', e.target.value)}
                          className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                            errors['nfe.valorTotal'] ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Ex: 275.00"
                        />
                        {errors['nfe.valorTotal'] && (
                          <p className="mt-1 text-sm text-red-600">{errors['nfe.valorTotal']}</p>
                        )}
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Chave de Acesso
                        </label>
                        <input
                          type="text"
                          value={formData.nfe.chaveAcesso}
                          onChange={(e) => handleNfeChange('chaveAcesso', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Ex: 12345678901234567890123456789012345678901234"
                        />
                      </div>
                    </div>
                    
                    {/* Cálculos de Custo por Rota */}
                    {formData.nfe.valorTotal && formData.hodometro && formData.quantidadeLitros && (
                      <div className="mt-6 bg-green-50 p-4 rounded-lg border border-green-200">
                        <h5 className="text-md font-medium text-green-800 mb-3">💰 Cálculos de Custo por Rota</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="text-center">
                            <p className="text-sm text-green-600">Custo por Litro</p>
                            <p className="text-lg font-semibold text-green-800">
                              R$ {formData.nfe.valorTotal && formData.quantidadeLitros ? 
                                (parseFloat(formData.nfe.valorTotal) / parseFloat(formData.quantidadeLitros)).toFixed(2) : '0.00'}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-green-600">Custo por Km</p>
                            <p className="text-lg font-semibold text-green-800">
                              R$ {formData.nfe.valorTotal && formData.hodometro ? 
                                (parseFloat(formData.nfe.valorTotal) / (parseInt(formData.hodometro) / 1000)).toFixed(2) : '0.00'}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-green-600">Consumo Médio</p>
                            <p className="text-lg font-semibold text-green-800">
                              {formData.quantidadeLitros && formData.hodometro ? 
                                (parseFloat(formData.quantidadeLitros) / (parseInt(formData.hodometro) / 1000)).toFixed(2) : '0.00'} L/km
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Upload da NF-e */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Arquivo da NF-e (PDF)
                      </label>
                      <input
                        type="file"
                        onChange={handleFileChange}
                        accept=".pdf"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Apenas arquivos PDF são permitidos. Tamanho máximo: 10MB
                      </p>
                      {formData.nfeArquivo && (
                        <p className="mt-1 text-sm text-green-600">
                          Arquivo selecionado: {formData.nfeArquivo.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Observações */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Observações
                    </label>
                    <textarea
                      value={formData.observacoes}
                      onChange={(e) => handleInputChange('observacoes', e.target.value)}
                      rows={3}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Observações adicionais sobre o abastecimento..."
                    />
                  </div>

                  {/* Botões */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={mutation.isLoading}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                    >
                      {mutation.isLoading ? 'Salvando...' : (abastecimento ? 'Atualizar' : 'Criar')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AbastecimentoFinanceiroForm;
